import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import VoidTemplates from '../../assets/images/edit-templates.png';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ListTemplates } from './components/list-templates';
import { ModalEditTemplate } from './components/modal-edit-template';
import { ModalNewTemplate } from './components/modal-new-template';
import { TemplateSelected } from './components/template';
import useTemplates from './documents-templates-hooks';

const useStyles = makeStyles((theme: any) => ({
  headerTitle: {
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  boldFont: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  listtitle: {},
  headerContainer: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottomColor: theme.accent.main,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
  },
  lettersPlaceholder: {
    color: theme.palette.tab.offselected,
    fontWeight: 400,
  },
  screen: {
    minWidth: 0,
  },
  listPane: {
    minWidth: 0,
    minHeight: 0,
  },
  detailPane: {
    minWidth: 0,
    minHeight: 0,
  },
  placeholder: {
    minHeight: theme.spacing(30),
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  listingState: {
    padding: theme.spacing(2),
  },
}));

export const DocumentsTemplates = () => {
  const {
    autoTemplates,
    open,
    setOpen,
    handleCreateTemplate,
    handleDeleteTemplate,
    setSelectedTemplate,
    selectedTemplate,
    handleUpdateTemplate,
    setOpenEditModal,
    openEditModal,
    tagsField,
    tagsImages,
    tagsSignatures,
    codeTemplates,
    partyRoles,
    transactionTypes,
    types,
    workflows,
    isLoading,
    isError,
    listingsLoading,
    listingsError,
    listingsReady,
    isCreating,
    isUpdating,
    isDeleting,
  } = useTemplates();

  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  const list = (
    <ListTemplates
      templates={autoTemplates}
      setOpen={setOpen}
      handleDeleteTemplate={handleDeleteTemplate}
      isDeleting={isDeleting}
      setSelectedTemplate={setSelectedTemplate}
      selectedTemplate={selectedTemplate}
      isLoading={isLoading}
      isError={isError}
      t={t}
    />
  );

  const detail = selectedTemplate ? (
    <TemplateSelected
      partyRoles={partyRoles}
      transactionTypes={transactionTypes}
      types={types}
      workflows={workflows}
      template={selectedTemplate}
      handleUpdateTemplate={handleUpdateTemplate}
      setOpenEditModal={setOpenEditModal}
      onBack={() => setSelectedTemplate(undefined)}
      showBackAction={isMobile}
      t={t}
      isSubmitting={isUpdating}
      listingsReady={listingsReady}
    />
  ) : (
    <Box
      className={classes.placeholder}
      display="flex"
      flex="1"
      padding={8}
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
    >
      <img src={VoidTemplates} alt="select-template" />
      <Typography variant="h5" className={classes.lettersPlaceholder} align="center">
        {t('documents:select-template')}
      </Typography>
    </Box>
  );

  return (
    <Box p={2} className={classes.screen}>
      {listingsError && (
        <Typography className={classes.listingState} color="error" role="status">
          {t('documents:template-options-error')}
        </Typography>
      )}
      {listingsLoading && !listingsError && (
        <Typography className={classes.listingState} color="textSecondary" role="status">
          {t('documents:template-options-loading')}
        </Typography>
      )}
      <Grid container direction="row" spacing={3}>
        {(!isMobile || !selectedTemplate) && (
          <Grid item xs={12} sm={4} md={3} lg={2} className={classes.listPane}>
            {list}
          </Grid>
        )}
        {(!isMobile || selectedTemplate) && (
          <Grid item xs={12} sm={8} md={9} lg={10} className={classes.detailPane}>
            {detail}
          </Grid>
        )}
      </Grid>
      <ModalNewTemplate
        open={open}
        setOpen={setOpen}
        partyRoles={partyRoles}
        transactionTypes={transactionTypes}
        types={types}
        workflows={workflows}
        createTemplate={handleCreateTemplate}
        codeTemplates={codeTemplates}
        t={t}
        isSubmitting={isCreating}
        listingsReady={listingsReady}
      />
      {openEditModal && (
        <ModalEditTemplate
          setOpenEditModal={setOpenEditModal}
          openEditModal={openEditModal}
          template={selectedTemplate}
          tagsField={tagsField}
          tagsImages={tagsImages}
          tagsSignatures={tagsSignatures}
        />
      )}
    </Box>
  );
};
